import React, { useCallback, useMemo } from 'react'
import { IonCol, IonGrid, IonItemDivider, IonLabel, IonList, IonRow } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import Validation from '../../../components/validation/Validation.component'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import {
  setBeforeIntakeCrys,
  setBeforeIntakeColl,
  setBeforeIntakeOther,
  setBeforeIntakeFeedings,
  setBeforeOutputEbl,
  setBeforeOutputUo,
  setBeforeOutputOther,
  setBeforeOutputBm,
  setDuringIntakeCrys,
  setDuringIntakeColl,
  setDuringIntakeOther,
  setDuringIntakeFeedings,
  setDuringOutputEbl,
  setDuringOutputUo,
  setDuringOutputOther,
  setDuringOutputBm,
} from '../store/fluids.actions'
import { FluidsConstants } from '../store/fluids.constants'
import { getFluidsSelector } from '../selectors'
import './Fluids.content.css'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { getGenericOptionsSelector } from '../../options/selectors'
import { getCustomFluidsOptionsSelector } from '../../options/selectors/getCustomFluidsOptions.selector'
import { IOption } from '../../../common/common.types'
import { setCustomFluidValue } from '../store/fluids.actions'
import {
  ICustomFluidFieldProps,
  ICustomFluidsSectionProps,
  IFluidsContentProps,
  INumberPadFieldProps,
  ITransportContentProps,
  ITransportFieldValues,
  ITransportFieldConstants,
  ITransportContentConfig,
} from '../store/fluids.types'

const NumberPadField: React.FC<INumberPadFieldProps> = ({
  elementId,
  label,
  dataTestId,
  value,
  onChange,
  action,
  showDivider = true,
}) => {
  const valuesArray = useMemo(() => value?.split('') || [], [value])

  return (
    <>
      <IonRow className='mb-1'>
        <IonCol className='picklist'>
          <NumberPad
            elementId={elementId}
            label={label}
            dataTestId={dataTestId}
            values={valuesArray}
            onNumPadClick={onChange}
            validCharacters={numberPadKeysWithDecimal}
            action={action}
          />
          <Validation id={action} />
        </IonCol>
      </IonRow>
      {showDivider && <hr className='field-divider' />}
    </>
  )
}

const CustomFluidField: React.FC<ICustomFluidFieldProps> = (props: ICustomFluidFieldProps) => {
  const { option, customFluids, transportPeriod, prefix, onValueChange } = props

  const customFluidEntry = customFluids.find(
    (entry) => entry.id === option.id && entry.transportPeriod === transportPeriod
  )
  const value = customFluidEntry?.value || ''
  const cardinalityKey = customFluidEntry?.key || ''

  const onChange = useCallback(
    (numbers: string[]) => {
      onValueChange(option.id, numbers.join(''), transportPeriod)
    },
    [option.id, transportPeriod, onValueChange]
  )

  return (
    <>
      <hr className='field-divider' />
      <IonRow className='mb-1'>
        <IonCol className='picklist'>
          <NumberPad
            elementId={`${prefix}Custom${option.id}`}
            label={`${option.name} (${option.unit})`}
            dataTestId={`${prefix}Custom${option.id}`}
            values={value?.split('') || []}
            onNumPadClick={onChange}
            validCharacters={numberPadKeysWithDecimal}
            action={FluidsConstants.SET_CUSTOM_FLUID_VALUE}
            actionKey={cardinalityKey}
          />
          <Validation id={FluidsConstants.SET_CUSTOM_FLUID_VALUE} cardinalityKey={cardinalityKey} />
        </IonCol>
      </IonRow>
    </>
  )
}

const CustomFluidsSection: React.FC<ICustomFluidsSectionProps> = ({
  options,
  customFluids,
  transportPeriod,
  prefix,
  onValueChange,
}) => {
  if (options.length === 0) {
    return null
  }

  return (
    <>
      {options.map((option) => (
        <CustomFluidField
          key={`${prefix}-custom-${option.id}`}
          option={option}
          customFluids={customFluids}
          transportPeriod={transportPeriod}
          prefix={prefix}
          onValueChange={onValueChange}
        />
      ))}
    </>
  )
}

const TransportContent: React.FC<ITransportContentProps & ITransportContentConfig> = ({
  dpFluidsColl,
  dpFluidsOther,
  isNeonatal,
  prefix,
  transportPeriod,
}) => {
  const { t } = useTranslation()
  const fluidsState = useSelector(getFluidsSelector)
  const genericOptions = useSelector(getGenericOptionsSelector)
  const customFluidsOptions = useSelector(getCustomFluidsOptionsSelector)
  const { yesNoOptions } = genericOptions

  const customIntakeFluids = customFluidsOptions.filter((option) => option.intakeOutput === 1)
  const customOutputFluids = customFluidsOptions.filter((option) => option.intakeOutput === 2)
  const dispatch = useDispatch()

  const isBefore = prefix === 'Before'

  let constants: ITransportFieldConstants
  if (isBefore) {
    constants = {
      SET_INTAKE_CRYS: FluidsConstants.SET_BEFORE_INTAKE_CRYS,
      SET_INTAKE_COLL: FluidsConstants.SET_BEFORE_INTAKE_COLL,
      SET_INTAKE_OTHER: FluidsConstants.SET_BEFORE_INTAKE_OTHER,
      SET_INTAKE_FEEDINGS: FluidsConstants.SET_BEFORE_INTAKE_FEEDINGS,
      SET_OUTPUT_EBL: FluidsConstants.SET_BEFORE_OUTPUT_EBL,
      SET_OUTPUT_UO: FluidsConstants.SET_BEFORE_OUTPUT_UO,
      SET_OUTPUT_OTHER: FluidsConstants.SET_BEFORE_OUTPUT_OTHER,
    }
  } else {
    constants = {
      SET_INTAKE_CRYS: FluidsConstants.SET_DURING_INTAKE_CRYS,
      SET_INTAKE_COLL: FluidsConstants.SET_DURING_INTAKE_COLL,
      SET_INTAKE_OTHER: FluidsConstants.SET_DURING_INTAKE_OTHER,
      SET_INTAKE_FEEDINGS: FluidsConstants.SET_DURING_INTAKE_FEEDINGS,
      SET_OUTPUT_EBL: FluidsConstants.SET_DURING_OUTPUT_EBL,
      SET_OUTPUT_UO: FluidsConstants.SET_DURING_OUTPUT_UO,
      SET_OUTPUT_OTHER: FluidsConstants.SET_DURING_OUTPUT_OTHER,
    }
  }

  let currentValues: ITransportFieldValues
  if (isBefore) {
    currentValues = {
      intakeCrys: fluidsState.beforeIntakeCrys,
      intakeColl: fluidsState.beforeIntakeColl,
      intakeOther: fluidsState.beforeIntakeOther,
      intakeFeedings: fluidsState.beforeIntakeFeedings,
      outputEbl: fluidsState.beforeOutputEbl,
      outputUo: fluidsState.beforeOutputUo,
      outputOther: fluidsState.beforeOutputOther,
      outputBm: fluidsState.beforeOutputBm,
    }
  } else {
    currentValues = {
      intakeCrys: fluidsState.duringIntakeCrys,
      intakeColl: fluidsState.duringIntakeColl,
      intakeOther: fluidsState.duringIntakeOther,
      intakeFeedings: fluidsState.duringIntakeFeedings,
      outputEbl: fluidsState.duringOutputEbl,
      outputUo: fluidsState.duringOutputUo,
      outputOther: fluidsState.duringOutputOther,
      outputBm: fluidsState.duringOutputBm,
    }
  }

  const handleCustomFluidValueChange = (optionId: number, value: string, transportPeriod: number) => {
    dispatch(setCustomFluidValue(value, optionId, transportPeriod))
  }

  const intakeCrysOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeIntakeCrys(value))
    } else {
      dispatch(setDuringIntakeCrys(value))
    }
  }

  const intakeCollOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeIntakeColl(value))
    } else {
      dispatch(setDuringIntakeColl(value))
    }
  }

  const intakeOtherOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeIntakeOther(value))
    } else {
      dispatch(setDuringIntakeOther(value))
    }
  }

  const outputEblOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeOutputEbl(value))
    } else {
      dispatch(setDuringOutputEbl(value))
    }
  }

  const outputUoOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeOutputUo(value))
    } else {
      dispatch(setDuringOutputUo(value))
    }
  }

  const outputOtherOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeOutputOther(value))
    } else {
      dispatch(setDuringOutputOther(value))
    }
  }

  const intakeFeedingsOnChange = (numbers: string[]) => {
    const value = numbers.join('')
    if (isBefore) {
      dispatch(setBeforeIntakeFeedings(value))
    } else {
      dispatch(setDuringIntakeFeedings(value))
    }
  }

  const outputBmOnChange = (option: IOption) => {
    if (isBefore) {
      dispatch(setBeforeOutputBm(option))
    } else {
      dispatch(setDuringOutputBm(option))
    }
  }

  return (
    <>
      <IonCol size='6' className='ion-padding-end'>
        <IonRow>
          <IonCol>
            <IonLabel className='fluids-column-header'>{t('fluids.Intake')}</IonLabel>
          </IonCol>
        </IonRow>
        <NumberPadField
          elementId={`${prefix}IntakeCrys`}
          label={t('fluids.CrysMl')}
          dataTestId={`${prefix}IntakeCrys`}
          value={currentValues.intakeCrys}
          onChange={intakeCrysOnChange}
          action={constants.SET_INTAKE_CRYS}
        />
        {dpFluidsColl > 0 && (
          <NumberPadField
            elementId={`${prefix}IntakeColl`}
            label={t('fluids.CollMl')}
            dataTestId={`${prefix}IntakeColl`}
            value={currentValues.intakeColl}
            onChange={intakeCollOnChange}
            action={constants.SET_INTAKE_COLL}
          />
        )}
        {dpFluidsOther > 0 && (
          <NumberPadField
            elementId={`${prefix}IntakeOther`}
            label={t('fluids.OtherMl')}
            dataTestId={`${prefix}IntakeOther`}
            value={currentValues.intakeOther}
            onChange={intakeOtherOnChange}
            action={constants.SET_INTAKE_OTHER}
            showDivider={false}
          />
        )}
        {isNeonatal && (
          <>
            <hr className='field-divider' />
            <NumberPadField
              elementId={`${prefix}IntakeFeedings`}
              label={t('fluids.Feedings')}
              dataTestId={`${prefix}IntakeFeedings`}
              value={currentValues.intakeFeedings}
              onChange={intakeFeedingsOnChange}
              action={constants.SET_INTAKE_FEEDINGS}
              showDivider={false}
            />
          </>
        )}
        <CustomFluidsSection
          options={customIntakeFluids}
          customFluids={fluidsState.customFluids}
          transportPeriod={transportPeriod}
          prefix={`${prefix}Intake`}
          onValueChange={handleCustomFluidValueChange}
        />
      </IonCol>
      <IonCol size='6' className='ion-padding-start'>
        <IonRow>
          <IonCol>
            <IonLabel className='fluids-column-header'>{t('fluids.Output')}</IonLabel>
          </IonCol>
        </IonRow>
        <NumberPadField
          elementId={`${prefix}OutputEbl`}
          label={t('fluids.EblMl')}
          dataTestId={`${prefix}OutputEbl`}
          value={currentValues.outputEbl}
          onChange={outputEblOnChange}
          action={constants.SET_OUTPUT_EBL}
        />
        <NumberPadField
          elementId={`${prefix}OutputUo`}
          label={t('fluids.UoMl')}
          dataTestId={`${prefix}OutputUo`}
          value={currentValues.outputUo}
          onChange={outputUoOnChange}
          action={constants.SET_OUTPUT_UO}
        />
        {dpFluidsOther > 0 && (
          <NumberPadField
            elementId={`${prefix}OutputOther`}
            label={t('fluids.OtherMl')}
            dataTestId={`${prefix}OutputOther`}
            value={currentValues.outputOther}
            onChange={outputOtherOnChange}
            action={constants.SET_OUTPUT_OTHER}
            showDivider={false}
          />
        )}
        {isNeonatal && (
          <>
            <hr className='field-divider' />
            <IonRow className='mb-1'>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID={`${prefix}OutputBm`}
                  selectedOption={currentValues.outputBm}
                  listHeader={t('fluids.BM')}
                  options={yesNoOptions}
                  action={outputBmOnChange}
                  label={t('fluids.BM')}
                />
              </IonCol>
            </IonRow>
          </>
        )}
        <CustomFluidsSection
          options={customOutputFluids}
          customFluids={fluidsState.customFluids}
          transportPeriod={transportPeriod}
          prefix={`${prefix}Output`}
          onValueChange={handleCustomFluidValueChange}
        />
      </IonCol>
    </>
  )
}

const FluidsContent: React.FC<IFluidsContentProps> = ({ transportType }) => {
  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { datapointsConfig } = configuration
  const { DP_FLUIDS_COLL, DP_FLUIDS_OTHER } = datapointsConfig
  const fluidsState = useSelector(getFluidsSelector)
  const { isNeonatal } = fluidsState

  const isBefore = transportType === 'before'
  const prefix = isBefore ? 'Before' : 'During'
  const transportPeriod = isBefore ? 1 : 2

  return (
    <IonList id='FluidsContent'>
      <IonGrid className='ion-no-padding'>
        <IonRow>
          <TransportContent
            key={transportType}
            dpFluidsColl={DP_FLUIDS_COLL}
            dpFluidsOther={DP_FLUIDS_OTHER}
            isNeonatal={isNeonatal}
            prefix={prefix}
            transportPeriod={transportPeriod}
          />
        </IonRow>
      </IonGrid>
      <IonItemDivider color='light' />
    </IonList>
  )
}

export default FluidsContent
